// ── AI Context Builder ──────────────────────────────────────────────────────
// Compresses diagram state into token-efficient text for the AI model.
// Three levels: digest (~100 tokens), topology (~300-800), region (~200-500).

import { useDiagramStore } from '../../stores/diagramStore';
import type { AIContext } from './types';

/** Rough token estimate: ~4 chars per token for English/JSON. */
function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/** Truncate text to a token budget. */
function truncateToTokenBudget(text: string, maxTokens: number): string {
  const maxChars = maxTokens * 4;
  if (text.length <= maxChars) return text;
  return text.slice(0, maxChars - 30) + '\n... (truncated)';
}

/** Labels of flow nodes that have no incident edges. */
export function unconnectedNodeLabels(
  nodes: Array<{ id: string; label: string; type: string }>,
  edges: Array<{ sourceNodeId: string; targetNodeId: string }>,
): string[] {
  const connected = new Set<string>();
  edges.forEach(e => {
    connected.add(e.sourceNodeId);
    connected.add(e.targetNodeId);
  });
  return nodes
    .filter(n => n.type !== 'group_input' && n.type !== 'group_output' && !connected.has(n.id))
    .map(n => n.label);
}

/** Level 0: One-liner stats. Always cheap. */
export function buildDigest(): string {
  const state = useDiagramStore.getState();
  const typeCounts = new Map<string, number>();
  state.nodes.forEach(n => typeCounts.set(n.type, (typeCounts.get(n.type) || 0) + 1));
  const typeStr = [...typeCounts.entries()].map(([t, c]) => `${c} ${t}`).join(', ');

  const activeName = state.pages.find(p => p.id === state.activePage)?.name ?? 'Main';
  const frameLabels = state.groups
    .filter(g => g.childNodeIds.length > 0)
    .map(g => `"${g.label}"`)
    .join(', ');
  const pageNames = state.pages.map(p => {
    const active = p.id === state.activePage ? '*' : '';
    return `"${p.name}"${active}`;
  }).join(', ');

  const parts = [`${state.diagramType} diagram "${activeName}"`];
  if (state.diagramType === 'flow') {
    parts.push(`${state.nodes.length} nodes (${typeStr || 'none'})`);
    parts.push(`${state.edges.length} edges`);
    if (state.groups.length > 0) parts.push(`${state.groups.length} frames [${frameLabels}]`);
  } else {
    parts.push(`${state.participants.length} participants`);
    parts.push(`${state.messages.length} messages`);
    if (state.fragments.length > 0) parts.push(`${state.fragments.length} fragments`);
  }
  if (state.pages.length > 1) parts.push(`${state.pages.length} pages [${pageNames}]`);
  if (state.catalog.length > 0) parts.push(`${state.catalog.length} catalog objects`);

  return parts.join(' | ');
}

/** Level 1: Compact adjacency using labels only. */
export function buildTopology(maxTokens = 800): string {
  const state = useDiagramStore.getState();

  if (state.diagramType === 'sequence') {
    return buildSequenceContext(maxTokens);
  }

  // Node list: "Label(type)" notation
  const nodeList = state.nodes
    .filter(n => n.type !== 'group_input' && n.type !== 'group_output')
    .map(n => `${n.label}(${n.type})`)
    .join(', ');

  // Edge list: arrow notation
  const edgeList = state.edges.map(e => {
    const src = state.nodes.find(n => n.id === e.sourceNodeId);
    const tgt = state.nodes.find(n => n.id === e.targetNodeId);
    if (!src || !tgt) return null;
    const label = e.label ? ` "${e.label}"` : '';
    return `${src.label}\u2192${tgt.label}${label}`;
  }).filter(Boolean).join(', ');

  // Frame membership
  const frameStr = state.groups
    .filter(g => g.childNodeIds.length > 0)
    .map(g => {
      const members = g.childNodeIds
        .map(id => state.nodes.find(n => n.id === id)?.label)
        .filter(Boolean)
        .join(', ');
      return `[${g.label}: ${members}]`;
    }).join(' ');

  // Page links
  const pageLinks = state.nodes
    .filter(n => n.linkedPageId)
    .map(n => {
      const page = state.pages.find(p => p.id === n.linkedPageId);
      return page ? `${n.label}\u2192page:"${page.name}"` : null;
    })
    .filter(Boolean)
    .join(', ');

  const isolated = unconnectedNodeLabels(state.nodes, state.edges);

  let result = `Nodes: ${nodeList || '(empty)'}\nEdges: ${edgeList || '(none)'}`;
  if (isolated.length > 0) {
    result += `\nUnconnected nodes (connect these if they belong in this architecture): ${isolated.join(', ')}`;
  }
  if (frameStr) result += `\nFrames: ${frameStr}`;
  if (pageLinks) result += `\nPage links: ${pageLinks}`;

  if (state.catalog.length > 0) {
    const catalogStr = state.catalog.map(entry => {
      const desc = entry.description ? `: ${entry.description.slice(0, 80)}` : '';
      return `${entry.name}(${entry.type}${entry.c4Level ? `,${entry.c4Level}` : ''}${desc})`;
    }).join(', ');
    result += `\nCatalog: ${catalogStr}`;
  }

  const otherPages = state.pages.filter(page => page.id !== state.activePage);
  if (otherPages.length > 0) {
    result += `\nOther pages: ${otherPages.map(page => {
      const n = page.nodes?.length ?? 0;
      const p = page.participants?.length ?? 0;
      const portals = page.nodes.filter(node => node.linkedPageId).map(node => node.label).join(', ');
      return `"${page.name}" (${page.diagramType ?? 'flow'}, ${n} nodes${p ? `, ${p} participants` : ''}${portals ? `; portals: ${portals}` : ''})`;
    }).join('; ')}`;
  }

  const groups = state.nodes.filter(n => n.childGraphId);
  if (groups.length > 0) {
    result += `\nArchitecture groups: ${groups.map(node => {
      const graph = state.graphs[node.childGraphId!];
      const inner = (graph?.nodes ?? [])
        .filter(child => child.type !== 'group_input' && child.type !== 'group_output')
        .map(child => child.label)
        .join(', ');
      return `${node.label}[${inner || 'empty'}]`;
    }).join('; ')}`;
  }

  return truncateToTokenBudget(result, maxTokens);
}

/** Level 1 for sequence diagrams. */
function buildSequenceContext(maxTokens: number): string {
  const state = useDiagramStore.getState();
  const parts = state.participants
    .sort((a, b) => a.order - b.order)
    .map(p => `${p.label}(${p.type})`)
    .join(', ');

  const msgs = state.messages
    .sort((a, b) => a.y - b.y)
    .map((m, i) => {
      const src = state.participants.find(p => p.id === m.sourceId);
      const tgt = state.participants.find(p => p.id === m.targetId);
      const type = m.type !== 'sync' ? ` [${m.type}]` : '';
      return `  ${i + 1}. ${src?.label}\u2192${tgt?.label} "${m.label}"${type}`;
    })
    .join('\n');

  const frags = state.fragments.map(f => {
    const pNames = f.participantIds
      .map(id => state.participants.find(p => p.id === id)?.label)
      .filter(Boolean)
      .join(', ');
    return `  ${f.type} "${f.label}" participants [${pNames}]`;
  }).join('\n');

  let result = `Participants: ${parts || '(none)'}\nMessages:\n${msgs || '  (none)'}`;
  if (frags) result += `\nFragments:\n${frags}`;

  return truncateToTokenBudget(result, maxTokens);
}

/** Level 2: Detailed view of specific nodes + 1-hop neighbors. */
export function buildRegionDetail(focusLabels: string[]): string {
  const state = useDiagramStore.getState();

  if (state.diagramType === 'sequence') {
    // For sequence, just return the full sequence context (usually small enough)
    return buildSequenceContext(500);
  }

  const focusNodes = state.nodes.filter(n =>
    focusLabels.some(f => n.label.toLowerCase().includes(f.toLowerCase()))
  );
  const focusIds = new Set(focusNodes.map(n => n.id));

  // 1-hop neighbors
  const neighborIds = new Set<string>();
  state.edges.forEach(e => {
    if (focusIds.has(e.sourceNodeId)) neighborIds.add(e.targetNodeId);
    if (focusIds.has(e.targetNodeId)) neighborIds.add(e.sourceNodeId);
  });

  const relevantNodes = state.nodes.filter(n =>
    focusIds.has(n.id) || neighborIds.has(n.id)
  );

  const lines = relevantNodes.map(n => {
    const isFocus = focusIds.has(n.id);
    const prefix = isFocus ? '\u25b8' : ' ';
    const pos = `@ (${Math.round(n.position.x)},${Math.round(n.position.y)})`;
    const sIn = n.sockets.filter(s => s.type === 'input').length;
    const sOut = n.sockets.filter(s => s.type === 'output').length;
    const linked = n.linkedPageId
      ? ` linkedPage:"${state.pages.find(p => p.id === n.linkedPageId)?.name}"`
      : '';
    return `${prefix} ${n.label}(${n.type}) ${pos} sockets:[in:${sIn},out:${sOut}]${linked}`;
  });

  const relevantIds = new Set([...focusIds, ...neighborIds]);
  const edgeLines = state.edges
    .filter(e => relevantIds.has(e.sourceNodeId) && relevantIds.has(e.targetNodeId))
    .map(e => {
      const src = state.nodes.find(n => n.id === e.sourceNodeId);
      const tgt = state.nodes.find(n => n.id === e.targetNodeId);
      const label = e.label ? ` "${e.label}"` : '';
      return `  ${src?.label}\u2192${tgt?.label}${label}`;
    });

  return `Region detail (\u25b8 = focus):\n${lines.join('\n')}\nLocal edges:\n${edgeLines.join('\n') || '  (none)'}`;
}

/** Extract existing node/participant labels mentioned in the user's message. */
export function extractMentionedLabels(userMessage: string): string[] {
  const state = useDiagramStore.getState();
  const msgLower = userMessage.toLowerCase();
  const allLabels = [
    ...state.nodes.map(n => n.label),
    ...state.participants.map(p => p.label),
    ...state.catalog.map(entry => entry.name),
  ].filter(l => l.length >= 2); // skip very short labels

  return allLabels.filter(label =>
    msgLower.includes(label.toLowerCase())
  );
}

/** Build the optimal context given budget and user message. */
export function buildAIContext(userMessage: string, maxTokens = 700): AIContext {
  const state = useDiagramStore.getState();
  const digest = buildDigest();
  const itemCount = state.diagramType === 'flow'
    ? state.nodes.length
    : state.participants.length;

  // Always include digest
  const remaining = maxTokens - estimateTokens(digest);

  // Check for mentioned labels → use region detail if found
  const mentioned = extractMentionedLabels(userMessage);

  if (mentioned.length > 0 && itemCount > 8) {
    const region = buildRegionDetail(mentioned);
    return { digest, regionDetail: truncateToTokenBudget(region, remaining) };
  }

  // Small diagrams: include full topology
  if (itemCount <= 25) {
    const topology = buildTopology(remaining);
    return { digest, topology };
  }

  // Large diagrams: just the digest (AI can request more on follow-up)
  return { digest };
}

/** Format AIContext into a single string for injection into the prompt. */
export function formatContextForPrompt(ctx: AIContext): string {
  let result = `CURRENT DIAGRAM: ${ctx.digest}`;
  if (ctx.topology) result += `\n${ctx.topology}`;
  if (ctx.regionDetail) result += `\n${ctx.regionDetail}`;
  return result;
}
